import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  Copy,
  ExternalLink,
  HelpCircle,
  History,
  MapPin,
  MessageCircle,
  Package,
  PackageCheck,
  PencilLine,
  Phone,
  Printer,
  Settings2,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDelivery } from '@/hooks/useDeliveries';
import {
  deliveryLabel,
  deliveryRpc,
  fulfillmentOptions,
  publicTrackingUrl,
  type DeliveryRow,
} from '@/lib/deliveries';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { Select } from '@/components/ui/FormControls';
import DeliveryUpdateDialog, { deliveryButton } from '@/components/delivery/DeliveryUpdateDialog';
import DeliveryDocuments from '@/components/delivery/DeliveryDocuments';
import DeliveryWhatsAppDialog from '@/components/delivery/DeliveryWhatsAppDialog';
import UserGuide from '@/components/help/UserGuide';
import {
  DeliveryMethod,
  ShipmentProgress,
  deliveryNextStep,
} from '@/components/delivery/DeliveryPresentation';

export default function DeliveryDetailPage() {
  const { saleId } = useParams();
  const { organization, profile, access } = useAuth();
  return (
    <DeliveryDetail
      key={`${organization?.id}:${profile?.id}:${access?.access_version}:${saleId}`}
      id={saleId}
    />
  );
}
function MethodForm({ row }: { row: DeliveryRow }) {
  const [method, setMethod] = useState(
    row.fulfillment_method === 'unspecified' ? '' : row.fulfillment_method,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { can } = useAuth();
  const client = useQueryClient();
  const navigate = useNavigate();
  return (
    <form
      className="space-y-3 p-4 sm:p-5"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy || !can('sales.status')) return;
        setBusy(true);
        setError('');
        try {
          await deliveryRpc('classify_sale_fulfillment', {
            p_data: {
              sale_id: row.sale_id,
              expected_sale_updated_at: row.sale_updated_at,
              fulfillment_method: method,
            },
          });
          await refreshSaleConsumers(client);
          toast.success('Delivery method saved.');
          if (method === 'pickup') navigate('/deliveries');
        } catch (failure) {
          setError(failure instanceof Error ? failure.message : 'Method could not be saved.');
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2 className="font-semibold">Delivery method</h2>
      <p className="text-sm text-muted-foreground">
        Choose how this sale reaches the customer. Pickup stays in Sales.
      </p>
      <label className="block text-sm">
        Method
        <Select
          className="w-full"
          required
          value={method}
          onChange={(e) => setMethod(e.target.value as typeof method)}
          disabled={busy || !can('sales.status')}
        >
          <option value="" disabled>
            Choose delivery method
          </option>
          {fulfillmentOptions.map((o) => (
            <option value={o.value} key={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </label>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <button className={deliveryButton} disabled={busy || !method || !can('sales.status')}>
        {busy ? 'Saving…' : 'Save delivery method'}
      </button>
    </form>
  );
}
function DeliveryDetail({ id }: { id?: string }) {
  const query = useDelivery(id);
  const { can } = useAuth();
  const [dialog, setDialog] = useState<'update' | 'whatsapp' | null>(null);
  const [snapshot, setSnapshot] = useState<DeliveryRow[]>([]);
  if (query.isPending) return <LoadingState />;
  if (query.error || !query.data)
    return (
      <div>
        <PageHeader title="Delivery" backTo="/deliveries" />
        <ErrorState
          title="Delivery could not be loaded"
          message={query.error?.message}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  const row = query.data;
  const tracking = publicTrackingUrl(row.tracking_url);
  const website = publicTrackingUrl(row.website_url);
  const locked = ['dispatched', 'in_transit', 'out_for_delivery', 'delivered'].includes(
    row.stage || '',
  );
  const open = (name: 'update' | 'whatsapp') => {
    setSnapshot([{ ...row }]);
    setDialog(name);
  };
  const date = (value: string) => new Date(value).toLocaleString();
  const copyTracking = async () => {
    if (!row.tracking_number) return;
    try {
      await navigator.clipboard.writeText(row.tracking_number);
      toast.success('Tracking number copied.');
    } catch {
      toast.error('Could not copy. Select the tracking number to copy it manually.');
    }
  };
  const unitCount = row.items.reduce((total, item) => total + item.quantity, 0);
  const eventLabels: Record<string, string> = {
    source_observed: 'Sale update recorded',
    status_change: 'Status updated',
    courier_update: 'Courier details updated',
    packing_invalidated: 'Packing needs recheck',
    reverify_packing: 'Packing rechecked',
    correct_status: 'Delivery stage corrected',
    set_issue: 'Delivery issue reported',
    resolve_issue: 'Delivery issue resolved',
  };
  return (
    <div className="space-y-5 pb-6">
      <header className="space-y-3">
        <Link
          to="/deliveries"
          className="inline-flex min-h-11 items-center gap-2 rounded-md text-xs font-medium text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All deliveries
        </Link>
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div className="min-w-0">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[.16em] text-muted-foreground">
              Shipment details
            </p>
            <h1 className="break-words text-xl font-bold tracking-tight sm:text-2xl">
              {row.sale_number}
            </h1>
            <p className="mt-1 break-words text-sm text-muted-foreground">
              {row.customer_name || 'Customer name not recorded'}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <UserGuide />
            <Link className={deliveryButton} to={`/sales/${row.sale_id}`}>
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              View sale & receipt
            </Link>
          </div>
        </div>
      </header>
      <div className="flex min-w-0 flex-col gap-5 xl:grid xl:items-start xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
        <div className="contents xl:block xl:min-w-0 xl:space-y-5">
          {row.stage ? (
            <section className="order-1 overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] via-card to-card p-4 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xs font-semibold text-muted-foreground">Shipment overview</h2>
                <DeliveryMethod method={row.fulfillment_method} />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-primary/10 text-cyan-700 dark:text-primary">
                  {row.stage === 'delivered' ? (
                    <PackageCheck className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Truck className="h-6 w-6" aria-hidden="true" />
                  )}
                </span>
                <div className="min-w-0">
                  <p className="text-xl font-semibold tracking-tight sm:text-2xl">
                    {deliveryLabel(row.stage)}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Sale status: {deliveryLabel(row.sale_status)}
                  </p>
                </div>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
                {deliveryNextStep(row)}
              </p>
              {(row.packing_requires_review || row.issue) && (
                <div className="mt-4 space-y-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-xs leading-relaxed text-amber-900 dark:text-amber-200">
                  {row.packing_requires_review && (
                    <p className="flex items-start gap-2">
                      <PackageCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
                      Items or recipient changed. Recheck the packed parcel before dispatch.
                    </p>
                  )}
                  {row.issue && (
                    <p className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                      Needs attention: {deliveryLabel(row.issue)}
                    </p>
                  )}
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                {can('sales.status') && (
                  <button
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    onClick={() => open('update')}
                  >
                    <PencilLine className="h-4 w-4" aria-hidden="true" />
                    Update delivery
                  </button>
                )}
                {can('sales.whatsapp') && (
                  <button className={deliveryButton} onClick={() => open('whatsapp')}>
                    <MessageCircle
                      className="h-4 w-4 text-emerald-700 dark:text-emerald-300"
                      aria-hidden="true"
                    />
                    WhatsApp update
                  </button>
                )}
              </div>
              <ShipmentProgress row={row} />
            </section>
          ) : (
            <section className="order-1 rounded-2xl border border-primary/20 bg-primary/5 p-5">
              <HelpCircle className="h-6 w-6 text-cyan-700 dark:text-primary" aria-hidden="true" />
              <h2 className="mt-3 font-semibold">Choose how this order will arrive</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {can('sales.status')
                  ? 'Set Courier or Local delivery to start preparing this shipment.'
                  : 'The delivery method has not been set yet.'}
              </p>
              {can('sales.status') && (
                <div className="-mx-5 -mb-5 mt-5 rounded-b-2xl border-t border-border bg-card">
                  <MethodForm key={row.sale_updated_at} row={row} />
                </div>
              )}
            </section>
          )}

          <section className="order-3 overflow-hidden rounded-2xl border border-border bg-card">
            <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Package className="h-4 w-4 text-cyan-700 dark:text-primary" aria-hidden="true" />
                  Items to pack
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Check these items before marking the parcel packed.
                </p>
              </div>
              <span className="shrink-0 rounded-lg bg-muted/60 px-2.5 py-1.5 text-xs font-semibold tabular-nums">
                {unitCount} {unitCount === 1 ? 'unit' : 'units'}
              </span>
            </header>
            {row.items.length ? (
              <ul className="divide-y divide-border">
                {row.items.map((item, index) => (
                  <li key={item.id} className="flex items-start gap-3 p-4 sm:px-5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/30 text-[11px] font-medium text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-medium leading-relaxed">{item.name}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {item.size && (
                          <span className="rounded-md bg-muted/60 px-2 py-1 text-[11px] text-muted-foreground">
                            Size: {item.size}
                          </span>
                        )}
                        {item.color && (
                          <span className="break-words rounded-md bg-muted/60 px-2 py-1 text-[11px] text-muted-foreground">
                            {item.color}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-sm font-semibold tabular-nums">
                      × {item.quantity}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-5 text-sm text-muted-foreground">
                No items are available. Review the original sale before preparing a parcel.
              </p>
            )}
          </section>

          <section className="order-6 overflow-hidden rounded-2xl border border-border bg-card">
            <header className="border-b border-border px-4 py-4 sm:px-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <History className="h-4 w-4 text-cyan-700 dark:text-primary" aria-hidden="true" />
                Delivery timeline
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Latest updates first. Earlier unrecorded milestones remain unknown.
              </p>
            </header>
            {!row.events.length ? (
              <div className="p-6 text-center">
                <Clock3 className="mx-auto h-7 w-7 text-muted-foreground/50" aria-hidden="true" />
                <p className="mt-3 text-sm text-muted-foreground">
                  No delivery events recorded yet.
                </p>
              </div>
            ) : (
              <ol className="space-y-5 p-4 sm:p-5">
                {row.events.map((event, index) => (
                  <li key={event.id} className="relative flex gap-3">
                    {index < row.events.length - 1 && (
                      <span
                        className="absolute bottom-[-20px] left-[15px] top-8 w-px bg-border"
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${event.action === 'set_issue' || event.action === 'packing_invalidated' ? 'border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-300' : index === 0 ? 'border-primary/20 bg-primary/10 text-cyan-700 dark:text-primary' : 'border-border bg-muted/30 text-muted-foreground'}`}
                    >
                      <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1 pb-1">
                      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                        <p className="text-sm font-semibold">
                          {eventLabels[event.action] || deliveryLabel(event.action)}
                        </p>
                        <span className="text-[11px] text-muted-foreground">
                          {deliveryLabel(event.to_stage)}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                        Recorded <time dateTime={event.recorded_at}>{date(event.recorded_at)}</time>
                        {event.actor_name ? ` · ${event.actor_name}` : ''}
                      </p>
                      {event.occurred_at && (
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          Actual time:{' '}
                          <time dateTime={event.occurred_at}>{date(event.occurred_at)}</time>
                        </p>
                      )}
                      {event.from_stage && event.from_stage !== event.to_stage && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          {deliveryLabel(event.from_stage)} <span aria-hidden="true">→</span>
                          <span className="sr-only">to</span> {deliveryLabel(event.to_stage)}
                        </p>
                      )}
                      {event.reason && (
                        <p className="mt-2 break-words text-xs leading-relaxed text-foreground/80">
                          {event.reason}
                        </p>
                      )}
                      {event.internal_note && (
                        <details className="mt-2 rounded-lg bg-muted/30 px-3">
                          <summary className="min-h-11 cursor-pointer py-3 text-xs font-medium text-muted-foreground">
                            Internal note
                          </summary>
                          <p className="whitespace-pre-wrap break-words pb-3 text-xs leading-relaxed">
                            {event.internal_note}
                          </p>
                        </details>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <aside
          className="contents xl:block xl:min-w-0 xl:space-y-5"
          aria-label="Recipient and shipment information"
        >
          <section className="order-2 overflow-hidden rounded-2xl border border-border bg-card">
            <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-4 sm:px-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <MapPin className="h-4 w-4 text-cyan-700 dark:text-primary" aria-hidden="true" />
                Deliver to
              </h2>
              {row.address_confirmation && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                  Checked
                </span>
              )}
            </header>
            <div className="space-y-4 p-4 sm:p-5">
              <div>
                <p className="break-words text-sm font-semibold">
                  {row.customer_name || 'Name not recorded'}
                </p>
                <address className="mt-2 whitespace-pre-wrap break-words text-sm not-italic leading-relaxed text-muted-foreground">
                  {row.customer_address ||
                    'Address not saved. Write the full address on the parcel and confirm it when packing.'}
                </address>
              </div>
              <div className="flex items-start gap-2 border-t border-border pt-3 text-sm">
                <Phone
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="break-all">
                  {row.customer_mobile || 'No customer mobile number'}
                </span>
              </div>
              <p className="flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {row.address_confirmation
                  ? deliveryLabel(row.address_confirmation)
                  : 'Address check pending'}
              </p>
              {can('sales.edit') && (
                <Link
                  to={`/sales/${row.sale_id}/edit`}
                  className="inline-flex min-h-11 items-center gap-2 text-xs font-semibold text-cyan-700 dark:text-primary"
                >
                  <PencilLine className="h-3.5 w-3.5" aria-hidden="true" />
                  Edit customer details in Sale
                </Link>
              )}
            </div>
          </section>

          <section className="order-4 overflow-hidden rounded-2xl border border-border bg-card">
            <header className="border-b border-border px-4 py-4 sm:px-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Truck className="h-4 w-4 text-cyan-700 dark:text-primary" aria-hidden="true" />
                Courier & tracking
              </h2>
            </header>
            <div className="space-y-4 p-4 sm:p-5">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-muted-foreground">
                  {row.fulfillment_method === 'local_delivery'
                    ? 'Delivery team'
                    : 'Courier company'}
                </p>
                <p className="mt-1.5 break-words text-sm font-medium">
                  {row.fulfillment_method === 'local_delivery'
                    ? 'Handled by your team'
                    : row.courier_name || 'Not assigned yet'}
                </p>
              </div>
              <div className="rounded-xl border border-dashed border-border bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-muted-foreground">
                    Tracking number
                  </p>
                  {row.tracking_number && (
                    <button
                      type="button"
                      aria-label="Copy tracking number"
                      title="Copy tracking number"
                      className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => void copyTracking()}
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
                <p className="mt-1 select-text break-all font-mono text-xs leading-relaxed">
                  {row.tracking_number ||
                    (row.fulfillment_method === 'local_delivery'
                      ? 'Not required for team delivery'
                      : 'Add when dispatching')}
                </p>
              </div>
              {(tracking || website) && (
                <div className="grid gap-2">
                  {tracking && (
                    <a
                      className={deliveryButton}
                      href={tracking}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Track parcel
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  )}
                  {website && (
                    <a
                      className="inline-flex min-h-11 items-center justify-center gap-2 text-xs font-medium text-cyan-700 dark:text-primary"
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Courier website
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="order-5 space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Printer className="h-4 w-4 text-cyan-700 dark:text-primary" aria-hidden="true" />
                Print & download
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Everything you need to prepare this order.
              </p>
            </div>
            <DeliveryDocuments rows={[row]} />
            <Link
              to="/print-templates"
              className="inline-flex min-h-11 items-center gap-2 text-xs font-medium text-cyan-700 dark:text-primary"
            >
              Blank templates & saved sender
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </section>

          {!locked && can('sales.status') && row.stage && (
            <details className="order-7 rounded-2xl border border-border bg-card">
              <summary className="flex min-h-12 cursor-pointer items-center justify-between gap-2 px-4 py-3 text-xs font-semibold sm:px-5">
                Delivery settings
                <Settings2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </summary>
              <div className="border-t border-border">
                <MethodForm key={row.sale_updated_at} row={row} />
              </div>
            </details>
          )}
        </aside>
      </div>
      {dialog === 'update' && (
        <DeliveryUpdateDialog
          rows={snapshot}
          onClose={() => setDialog(null)}
          onComplete={() => {}}
        />
      )}
      {dialog === 'whatsapp' && (
        <DeliveryWhatsAppDialog rows={snapshot} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
